import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Layout from "./Layout";
import usePageMeta from "../../hooks/usePageMeta";
import ActionDropdown from "../../components/ActionDropdown";
import DocumentPreviewModal from "../../components/DocumentPreviewModal";
import DateInput from "../../components/DateInput";
import { AtlasListToolbar } from "../../components/list-toolbar";
import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  TEMP_STAFF_FILES_DEFAULT_FILTERS,
  TEMP_STAFF_FILES_SORT_OPTIONS,
  buildTempStaffFilesFilterChips,
  TempStaffFilesFilterFields,
  TEMP_STAFF_FORMS_DEFAULT_FILTERS,
  TEMP_STAFF_FORMS_SORT_OPTIONS,
  buildTempStaffFormsFilterChips,
  TempStaffFormsFilterFields,
} from "../../components/filters";
import { MdEmail } from "react-icons/md";
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import ResetPasswordForm from "../../components/forms/ResetPasswordForm";
import TrustedDevicesForm from "../../components/forms/TrustedDevicesForm";
import TwoStepVerificationSection from "../../components/twoFactor/TwoStepVerificationSection";
import AddSkillForm from "../components/AddSkillForm";
import AddCertificateForm from "../components/AddCertificateForm";
import AddFileForm from "../components/AddFileForm";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { BASE_URL } from "../../config";
import useAuth from "../../hooks/useAuth";
import { showSuccess, showError, showConfirm } from "../../utils/dialog";
import {
  STAFF_DOCUMENT_ACCEPT,
  validateStaffDocumentFile,
} from "../../utils/staffDocumentValidation";
import {
  formatAppDate,
  formatAppDateTime,
  parseDbDateTime,
} from "../../utils/appDateTime";
import { DEFAULT_AVATAR_SRC, resolveBackendUrl } from "../../utils/accountAvatarUrl";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import {
  getOsIcon,
  getBrowserIcon,
  getDeviceTypeIcon,
  formatDeviceInfo,
} from "../../utils/deviceIcons";
import BackButton from "../../components/BackButton";
import { certDisplayName } from "../../utils/registrationProvince";
import { FaTimes } from "react-icons/fa";
import ProfilePhotoInput from "../../components/ProfilePhotoInput";

function normalizeFileType(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function normalizeFormStateFilter(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function normalizeFileStateFilter(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (["pending review", "pendingreview", "pending_review"].includes(raw)) {
    return "pending-review";
  }
  if (raw === "active") return "active";
  if (raw === "inactive") return "inactive";
  if (raw === "deleted") return "deleted";
  return normalizeFileType(raw);
}

// Residency mapping helpers
const mapServerResidencyToUI = (val) => {
  if (!val && val !== "") return val;
  const s = String(val || "").trim();
  switch (s) {
    case "study_resident":
    case "study_permit":
    case "study_res":
      return "study_permit";
    case "temporary_resident":
    case "work_permit":
    case "temp_res":
      return "work_permit";
    case "other":
    case "refugee":
      return "refugee";
    case "citizen":
    case "canadian":
      return "canadian";
    default:
      return s;
  }
};

const mapUIResidencyToServer = (val) => {
  if (!val && val !== "") return val;
  const s = String(val || "").trim();
  switch (s) {
    case "study_permit":
      return "study_resident";
    case "work_permit":
      return "temporary_resident";
    case "refugee":
      return "other";
    case "canadian":
      return "citizen";
    default:
      return s;
  }
};

const normalizeTempStaffAddressValue = (val) => {
  if (!val) {
    return {
      address: "",
      street: "",
      address2: "",
      city: "",
      region: "",
      zip: "",
      country: "",
    };
  }

  if (typeof val === "string") {
    return {
      address: val,
      street: val,
      address2: "",
      city: "",
      region: "",
      zip: "",
      country: "",
    };
  }

  const street = String(val.street || "").trim();
  const address2 = String(val.address2 || "").trim();
  const city = String(val.city || "").trim();
  const region = String(val.region || "").trim();
  const zip = String(val.zip || "").trim();
  const country = String(val.country || "").trim();

  return {
    address: [address2, street, city, region, zip, country]
      .filter(Boolean)
      .join(", "),
    street,
    address2,
    city,
    region,
    zip,
    country,
  };
};

function TempStaffEditFileModal({
  isOpen,
  fileRow,
  fileTypeCatalog,
  onClose,
  onDownload,
  onPreview,
  onRequestChange,
  saving = false,
}) {
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [replacementFile, setReplacementFile] = useState(null);
  const [selectedExpiryDate, setSelectedExpiryDate] = useState("");

  useEffect(() => {
    if (!isOpen || !fileRow) return;
    setSelectedTypeId(
      fileRow.type_id !== undefined && fileRow.type_id !== null
        ? String(fileRow.type_id)
        : "",
    );
    setReplacementFile(null);
    setSelectedExpiryDate(
      fileRow.expiry_date ? String(fileRow.expiry_date).slice(0, 10) : "",
    );
  }, [isOpen, fileRow]);

  if (!isOpen || !fileRow) return null;

  const selectedType =
    fileTypeCatalog.find((type) => String(type.id) === String(selectedTypeId)) ||
    null;
  const isSinDocumentSelected =
    String(selectedType?.type || "").trim() === "SIN Document";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9990] px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <h2 className="text-[22px] font-medium text-gray-800">Edit File</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="mb-6">
          <label className="block text-sm text-gray-500 mb-2">Type*</label>
          <select
            value={selectedTypeId}
            onChange={(e) => setSelectedTypeId(e.target.value)}
            className="w-full border-0 border-b border-gray-300 px-0 py-2 text-[16px] text-gray-800 bg-white focus:outline-none focus:border-gray-500"
          >
            <option value="">Select file type</option>
            {fileTypeCatalog.map((type) => (
              <option key={type.id} value={String(type.id)}>
                {type.type}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-8">
          {isSinDocumentSelected ? (
            <div className="mb-6">
              <label className="block text-sm text-gray-500 mb-2">
                Expiry date (optional)
              </label>
              <DateInput
                value={selectedExpiryDate}
                onChange={setSelectedExpiryDate}
                className="w-full border-0 border-b border-gray-300 px-0 py-2 text-[16px] text-gray-800 bg-white focus:outline-none focus:border-gray-500"
              />
            </div>
          ) : null}

          <label className="block text-sm text-gray-500 mb-2">File*</label>
          <label className="border border-gray-200 rounded-md min-h-[190px] flex flex-col items-center justify-center text-center px-6 py-8 bg-gray-50/40 cursor-pointer hover:border-gray-300">
            <div className="text-[72px] leading-none text-gray-300 mb-3">
              PDF
            </div>
            <div className="text-sm text-gray-600 break-all">
              {replacementFile?.name || fileRow.name || "Unnamed file"}
            </div>
            <div className="text-xs text-gray-400 mt-3">
              {replacementFile ? "Replacement selected" : "Click to re-upload this file"}
            </div>
            <input
              type="file"
              accept={STAFF_DOCUMENT_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                const validationMessage = file && validateStaffDocumentFile(file);
                if (validationMessage) {
                  showError(validationMessage);
                  e.target.value = "";
                  setReplacementFile(null);
                  return;
                }
                setReplacementFile(file);
              }}
            />
          </label>
        </div>

        <div className="flex items-center gap-10 mb-4 text-[15px]">
          <button
            type="button"
            disabled={saving || !selectedTypeId}
            onClick={() =>
              onRequestChange({
                fileRow,
                nextTypeId: selectedTypeId,
                nextTypeLabel: selectedType?.type || "",
                replacementFile,
                nextExpiryDate:
                  isSinDocumentSelected && selectedExpiryDate
                    ? selectedExpiryDate
                    : "",
              })
            }
            className="text-red-600 hover:text-red-700 disabled:text-gray-400"
          >
            {saving ? "REQUESTING..." : "REQUEST CHANGE"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-700 hover:text-gray-900"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={() => onDownload(fileRow)}
            className="text-gray-700 hover:text-gray-900"
          >
            DOWNLOAD
          </button>
          <button
            type="button"
            onClick={() => onPreview(fileRow)}
            className="text-gray-700 hover:text-gray-900"
          >
            PREVIEW
          </button>
        </div>

        <p className="text-sm text-gray-600">
          "Requested change will be sent to our team to review and update."
        </p>
      </div>
    </div>
  );
}

const MyAccount_tempStaff = () => {
  usePageMeta("MyAccount");

  const [certificateData, setCertificateData] = useState([]);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const getDisplayFileName = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (!/^https?:\/\//i.test(raw)) return raw;
    try {
      const clean = raw.split("?")[0].split("#")[0];
      return decodeURIComponent(clean.split("/").pop() || clean);
    } catch {
      return raw;
    }
  };
  const handleDownloadCertificateFile = async (row) => {
    const accountId = auth?.accountId || localStorage.getItem("accountId");
    if (!accountId || !row?.id) return;
    try {
      // The download route now requires login -- send the token like the file/form downloads do.
      const token = auth?.token || localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/api/personnel/accounts/${accountId}/certifications/${row.id}/file/download`,
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to download certificate file");
      }
      const blob = await response.blob();
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = getDisplayFileName(row.file) || `${row.name || "certificate"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Certificate download failed", error);
      showError(error.message || "Failed to download certificate file");
    }
  };
  const [skillData, setSkillData] = useState([]);
  // files fetched from backend for Files tab
  const [fileData, setFileData] = useState([]);
  const [formsData, setFormsData] = useState([]);

  const [selectedTab, setSelectedTab] = useState("GENERAL INFORMATION");
  const [searchTerm, setSearchTerm] = useState("");
  const [formSearchTerm, setFormSearchTerm] = useState("");
  const fileFilterPanel = useFilterPanel();
  const formFilterPanel = useFilterPanel();
  const [openModal, setOpenModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qualificationsLoading, setQualificationsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileTypeFilters, setFileTypeFilters] = useState(
    TEMP_STAFF_FILES_DEFAULT_FILTERS.fileTypeFilters,
  );
  const [fileStatusFilters, setFileStatusFilters] = useState(
    TEMP_STAFF_FILES_DEFAULT_FILTERS.statusFilters,
  );
  const [fileSortBy, setFileSortBy] = useState(TEMP_STAFF_FILES_DEFAULT_FILTERS.sortBy);
  const [fileSortDirection, setFileSortDirection] = useState(
    TEMP_STAFF_FILES_DEFAULT_FILTERS.sortDirection,
  );
  const [filePageSize, setFilePageSize] = useState(25);
  const [fileTypeCatalog, setFileTypeCatalog] = useState([]);
  const [editingFileRow, setEditingFileRow] = useState(null);
  const [previewFileRow, setPreviewFileRow] = useState(null);
  const [formStateFilters, setFormStateFilters] = useState(
    TEMP_STAFF_FORMS_DEFAULT_FILTERS.stateFilters,
  );
  const [formSortBy, setFormSortBy] = useState(TEMP_STAFF_FORMS_DEFAULT_FILTERS.sortBy);
  const [formSortDirection, setFormSortDirection] = useState(
    TEMP_STAFF_FORMS_DEFAULT_FILTERS.sortDirection,
  );
  const [formPageSize, setFormPageSize] = useState(25);
  const [securityData, setSecurityData] = useState({
    email: "",
    last_signin: "",
    two_factor_enabled: false,
    devices: [],
  });
  const auth = useAuth();

  const accountId = auth?.accountId || localStorage.getItem("accountId");
  const [showPassword, setShowPassword] = useState(false);

  // Form state – mapped to backend fields
  const [form, setForm] = useState({
    preferred_name: "",
    birthdate: "",
    first_name: "",
    last_name: "",
    middle_name: "",
    email: "",
    gender: "",
    phone_mobile: "",
    street: "",
    address2: "",
    city: "",
    region: "",
    zip: "",
    country: "",
    emergency_name: "",
    emergency_phone: "",
    personal_number: "",
    personal_email: "",
    residency_status: "",
    residency_document: "",
    residency_document_name: "",
    residency_document_url: "",
    hst_number: "",
    cheque_method: "delivery_pickup",
    applicable_tax_form: "",
    sin: "",
    signin_date: "",
    google_2fa_secret_key: "",
    force_2fa: "",
    profile_picture: "",
  });
  const [originalProfile, setOriginalProfile] = useState(null);

  const formatFileState = (state) => {
    const normalized = String(state || "").trim().toLowerCase();
    if (!normalized) return "—";
    if (["pending review", "pendingreview", "pending_review"].includes(normalized)) {
      return "Pending Review";
    }
    if (normalized === "active") return "Active";
    if (normalized === "inactive") return "Inactive";
    if (normalized === "deleted") return "Deleted";
    return normalized.replace(/\b\w/g, (m) => m.toUpperCase());
  };

  const fileTypeOptions = useMemo(() => {
    return [...new Set(fileData.map((row) => String(row.type || "").trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [fileData]);

  const fileTypeFilterOptions = useMemo(
    () =>
      fileTypeOptions.map((label) => ({
        value: normalizeFileType(label),
        label,
      })),
    [fileTypeOptions],
  );

  const filteredFileRows = useMemo(() => {
    const term = String(searchTerm || "").trim().toLowerCase();
    return fileData.filter((row) => {
      const normalizedState = formatFileState(row.state);
      const isDeleted = normalizedState.toLowerCase() === "deleted";

      if (isDeleted) {
        return false;
      }

      if (
        fileStatusFilters.length > 0 &&
        !fileStatusFilters.includes(normalizeFileStateFilter(row.state))
      ) {
        return false;
      }

      if (
        fileTypeFilters.length > 0 &&
        !fileTypeFilters.includes(normalizeFileType(row.type))
      ) {
        return false;
      }

      if (term) {
        const haystack = [
          row.name,
          row.note,
          row.type,
          normalizedState,
          row.create_date,
        ]
          .map((value) => String(value || "").toLowerCase())
          .join(" ");

        if (!haystack.includes(term)) return false;
      }

      return true;
    });
  }, [fileData, searchTerm, fileStatusFilters, fileTypeFilters]);

  const sortedFileRows = useMemo(() => {
    const rows = [...filteredFileRows];
    rows.sort((a, b) => {
      const getValue = (row) => {
        if (fileSortBy === "state") return formatFileState(row.state);
        if (fileSortBy === "type") return row.type || "";
        if (fileSortBy === "create_date") return row.create_date || "";
        return row.name || "";
      };

      let x = getValue(a);
      let y = getValue(b);

      if (fileSortBy === "create_date") {
        x = parseDbDateTime(x)?.getTime() ?? 0;
        y = parseDbDateTime(y)?.getTime() ?? 0;
      } else {
        x = String(x || "").toLowerCase();
        y = String(y || "").toLowerCase();
      }

      if (x < y) return fileSortDirection === "asc" ? -1 : 1;
      if (x > y) return fileSortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [filteredFileRows, fileSortBy, fileSortDirection]);

  const pagedFileRows = useMemo(
    () => sortedFileRows.slice(0, filePageSize),
    [sortedFileRows, filePageSize],
  );

  const sortedFormRows = useMemo(() => {
    const term = String(formSearchTerm || "").trim().toLowerCase();
    const rows = formsData.filter((row) => {
      if (
        formStateFilters.length > 0 &&
        !formStateFilters.includes(normalizeFormStateFilter(row.state))
      ) {
        return false;
      }

      if (term) {
        const haystack = [row.name, row.state, row.submit_date]
          .map((value) => String(value || "").toLowerCase())
          .join(" ");
        if (!haystack.includes(term)) return false;
      }

      return true;
    });

    rows.sort((a, b) => {
      const getValue = (row) => {
        if (formSortBy === "state") return String(row.state || "").toLowerCase();
        if (formSortBy === "submit_date") return row.submit_date || "";
        return String(row.name || "").toLowerCase();
      };

      let x = getValue(a);
      let y = getValue(b);

      if (formSortBy === "submit_date") {
        x = parseDbDateTime(x)?.getTime() ?? 0;
        y = parseDbDateTime(y)?.getTime() ?? 0;
      }

      if (x < y) return formSortDirection === "asc" ? -1 : 1;
      if (x > y) return formSortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return rows;
  }, [formsData, formSearchTerm, formStateFilters, formSortBy, formSortDirection]);

  const pagedFormRows = useMemo(
    () => sortedFormRows.slice(0, formPageSize),
    [sortedFormRows, formPageSize],
  );

  const formStateOptions = useMemo(() => {
    const labels = [
      ...new Set(
        formsData
          .map((row) => String(row.state || "").trim())
          .filter(Boolean),
      ),
    ].sort((a, b) => a.localeCompare(b));

    return labels.map((label) => ({
      value: normalizeFormStateFilter(label),
      label: label.charAt(0).toUpperCase() + label.slice(1),
    }));
  }, [formsData]);

  const fileFilterChips = useMemo(
    () =>
      buildTempStaffFilesFilterChips({
        fileTypeFilters,
        statusFilters: fileStatusFilters,
        fileTypeOptions: fileTypeFilterOptions,
      }),
    [fileTypeFilters, fileStatusFilters, fileTypeFilterOptions],
  );

  const fileResultsStart = sortedFileRows.length ? 1 : 0;
  const fileResultsEnd = Math.min(sortedFileRows.length, filePageSize);

  const clearFileFilters = () => {
    setFileTypeFilters(TEMP_STAFF_FILES_DEFAULT_FILTERS.fileTypeFilters);
    setFileStatusFilters(TEMP_STAFF_FILES_DEFAULT_FILTERS.statusFilters);
    setFileSortBy(TEMP_STAFF_FILES_DEFAULT_FILTERS.sortBy);
    setFileSortDirection(TEMP_STAFF_FILES_DEFAULT_FILTERS.sortDirection);
  };

  const removeFileFilterChip = (chip) => {
    const key = chip?.key;
    if (key === "fileTypeFilters") setFileTypeFilters([]);
    if (key === "statusFilters") setFileStatusFilters([]);
  };

  const formFilterChips = useMemo(
    () =>
      buildTempStaffFormsFilterChips({
        stateFilters: formStateFilters,
        stateOptions: formStateOptions,
      }),
    [formStateFilters, formStateOptions],
  );

  const formResultsStart = sortedFormRows.length ? 1 : 0;
  const formResultsEnd = Math.min(sortedFormRows.length, formPageSize);

  const clearFormFilters = () => {
    setFormStateFilters(TEMP_STAFF_FORMS_DEFAULT_FILTERS.stateFilters);
    setFormSortBy(TEMP_STAFF_FORMS_DEFAULT_FILTERS.sortBy);
    setFormSortDirection(TEMP_STAFF_FORMS_DEFAULT_FILTERS.sortDirection);
  };

  const removeFormFilterChip = (chip) => {
    const key = chip?.key;
    if (key === "stateFilters") setFormStateFilters([]);
  };

  const renderFileSortArrow = (field) => {
    if (fileSortBy !== field) return <span className="ml-1 opacity-70">⇅</span>;
    return (
      <span className="ml-1">{fileSortDirection === "asc" ? "▲" : "▼"}</span>
    );
  };

  const handleFileHeaderSort = (field) => {
    if (fileSortBy === field) {
      setFileSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setFileSortBy(field);
    setFileSortDirection("asc");
  };

  const renderFormSortArrow = (field) => {
    if (formSortBy !== field) return <span className="ml-1 opacity-70">⇅</span>;
    return (
      <span className="ml-1">{formSortDirection === "asc" ? "▲" : "▼"}</span>
    );
  };

  const handleFormHeaderSort = (field) => {
    if (formSortBy === field) {
      setFormSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setFormSortBy(field);
    setFormSortDirection("asc");
  };

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target?.value }));
  };

  const loadSecurityData = async () => {
    try {
      console.log("🔍 Loading security data...");
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      const token = auth?.token || localStorage.getItem("token");
      console.log(
        "🔑 Using accountId:",
        accountId,
        "token:",
        token ? "present" : "missing"
      );

      const url = `${BASE_URL}/api/my-account/account/security${accountId ? `?accountId=${accountId}` : ""
        }`;
      console.log("🌐 Fetching from:", url);

      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const j = await res.json();
      console.log("📊 Security data response:", j);

      if ((res.ok || j?.success) && j?.data) {
        setSecurityData(j.data);

        console.log("✅ Security data loaded:", j.data);
        console.log("📱 Devices data:", j.data.devices);
        if (j.data.devices && j.data.devices.length > 0) {
          console.log(
            "🔍 Sample device:",
            JSON.stringify(j.data.devices[0], null, 2)
          );
        }
      } else {
        console.warn("⚠️ Security data load failed:", j);
      }
    } catch (e) {
      console.error("❌ Security data load error", e);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const accountId = auth?.accountId || localStorage.getItem("accountId");
        const token = auth?.token || localStorage.getItem("token");
        const url = `${BASE_URL}/api/my-account/account/me${accountId ? `?accountId=${accountId}` : ""
          }`;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const j = await res.json();
        if ((res.ok || j?.success) && j?.data) {
          const d = j.data;
          setForm((prev) => ({
            ...prev,
            preferred_name: d.preferred_name || "",
            birthdate: d.birthdate ? d.birthdate.slice(0, 10) : "",
            first_name: d.first_name || "",
            last_name: d.last_name || "",
            middle_name: d.middle_name || "",
            email: d.email || "",
            gender: d.gender || "",
            phone_mobile: d.phone_mobile || "",
            street: d.street || "",
            address2: d.address2 || "",
            city: d.city || "",
            region: d.region || "",
            zip: d.zip || "",
            country: d.country || "",
            emergency_name: d.emergency_name || "",
            emergency_phone: d.emergency_phone || "",
            personal_number: d.personal_number || "",
            personal_email: d.personal_email || "",
            residency_status: mapServerResidencyToUI(d.residency_status || ""),
            // account may include linked file ids for permits
            residency_document:
              d.work_permit_file_id ||
              d.study_permit_file_id ||
              prev.residency_document ||
              "",
            residency_document_name: prev.residency_document_name || "",
            residency_document_url: prev.residency_document_url || "",
            hst_number: d.hst_number || "",
            cheque_method: d.cheque_method || "",
            applicable_tax_form: d.applicable_tax_form || "",
            sin: d.sin || "",
            signin_date: d.signin_date || "",
            google_2fa_secret_key: d.google_2fa_secret_key || "",
            force_2fa: d.force_2fa || "",
            profile_picture: d.profile_picture || "",
          }));
          // store original profile for change detection
          setOriginalProfile(d);
          // hydrate any pending requested attachments saved in localStorage
          try {
            const accountIdLocal =
              auth?.accountId || localStorage.getItem("accountId");
            const key = `requested_attachments_${accountIdLocal}`;
            const raw = localStorage.getItem(key);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed) && parsed.length > 0) {
                const url = parsed[0];
                setForm((prev) => ({
                  ...prev,
                  residency_document_url: prev.residency_document_url || url,
                  residency_document_name:
                    prev.residency_document_name || url.split("/").pop() || "",
                }));
              }
            }
          } catch (e) {
            console.warn("Failed to hydrate requested attachments", e);
          }
        } else {
          console.warn("MyAccount: no profile data", j);
        }

        // Load security data
        await loadSecurityData();
      } catch (e) {
        console.error("MyAccount load error", e);
      } finally {
        setLoading(false);
      }
    };
    // Only attempt when we have at least accountId or token ready
    if (
      auth ||
      localStorage.getItem("accountId") ||
      localStorage.getItem("token")
    ) {
      load();
    }
  }, [auth?.accountId, auth?.token]);

  // Load skills and certifications for the current account
  const loadQualifications = async () => {
    setQualificationsLoading(true);
    try {
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      if (!accountId) return;

      const skillsRes = await fetch(
        `${BASE_URL}/api/personnel/accounts/${accountId}/skills`,
        { credentials: "include" }
      );
      const skillsJson = await skillsRes.json().catch(() => ({}));
      const skills = Array.isArray(skillsJson.data)
        ? skillsJson.data
        : Array.isArray(skillsJson)
          ? skillsJson
          : [];
      // normalize to { skill_id, skill_name }
      setSkillData(
        skills.map((s) => ({
          skill_id: s.skill_id ?? s.id ?? s.skill_id,
          skill_name: s.skill_name ?? s.name ?? s.skill,
        }))
      );

      const certRes = await fetch(
        `${BASE_URL}/api/personnel/accounts/${accountId}/certifications`,
        { credentials: "include" }
      );
      const certJson = await certRes.json().catch(() => ({}));
      const certs = Array.isArray(certJson.data)
        ? certJson.data
        : Array.isArray(certJson)
          ? certJson
          : [];
      setCertificateData(
        certs.map((c) => ({
          id: c.employee_certification_id ?? c.id,
          name: c.certification_name ?? c.name,
          file:
            c.file_name ||
            (() => {
              const raw = c.file_url ?? c.file ?? "";
              if (!raw) return "";
              try {
                const clean = String(raw).split("?")[0].split("#")[0];
                return decodeURIComponent(clean.split("/").pop() || clean);
              } catch {
                return String(raw);
              }
            })(),
          expDate: c.expiry_date || c.expiry || "",
        }))
      );

      // Load files for Files tab
      try {
        const fRes = await fetch(
          `${BASE_URL}/api/personnel/accounts/${accountId}/files`,
          { credentials: "include" }
        );
        const fJ = await fRes.json().catch(() => ({}));
        const fRows = Array.isArray(fJ.data)
          ? fJ.data
          : Array.isArray(fJ)
            ? fJ
            : [];
        // normalize: { file_id, file_url, note, state, file_name, create_date, size, extension, mime_type }
        setFileData(
          fRows.map((f) => {
            const url = f.file_url || f.name || "";
            const inferredName = f.file_name || (url ? url.split("/").pop() : "");
            return {
              id: f.file_id || f.fileId || f.file_id,
              type_id: f.type_id ?? f.typeId ?? null,
              url,
              note: f.note || "",
              state: f.state || "",
              name: inferredName || f.file_name || "",
              type: f.type_name || f.type || f.type_class || f.type_id || "",
              typeLabel: f.file_name || f.type_name || "",
              create_date: f.create_date || "",
              size: f.size || 0,
              extension: f.extension || "",
              mime_type: f.mime_type || "",
              expiry_date: f.expiry_date || "",
              is_expired: Boolean(f.is_expired),
            };
          })
        );
      } catch (fe) {
        console.error("Failed to load files", fe);
      }

      try {
        const typeRes = await fetch(`${BASE_URL}/api/personnel/file-types`, {
          credentials: "include",
        });
        const typeJson = await typeRes.json().catch(() => ({}));
        const typeRows = Array.isArray(typeJson.data)
          ? typeJson.data
          : Array.isArray(typeJson)
            ? typeJson
            : [];
        setFileTypeCatalog(
          typeRows.map((row) => ({
            id: row.type_id || row.id,
            type: row.name || row.type || "",
          })),
        );
      } catch (typeErr) {
        console.error("Failed to load file types", typeErr);
      }

      // Load forms for Forms tab
      try {
        const fmRes = await fetch(
          `${BASE_URL}/api/personnel/accounts/${accountId}/forms`,
          { credentials: "include" }
        );
        const fmJ = await fmRes.json().catch(() => ({}));
        const fmRows = Array.isArray(fmJ.data)
          ? fmJ.data
          : Array.isArray(fmJ)
            ? fmJ
            : [];
        setFormsData(
          fmRows.map((r) => ({
            form_id: r.form_id || r.formId,
            name: r.form_name || r.name,
            submission_id: r.submission_id || r.form_submission_id,
            state: r.form_state || r.state,
            submit_date: r.submit_date || r.create_date,
          }))
        );
      } catch (fe) {
        console.error("Failed to load forms", fe);
      }
    } catch (e) {
      console.error("Failed to load qualifications", e);
    } finally {
      setQualificationsLoading(false);
    }
  };

  useEffect(() => {
    loadQualifications();
  }, [auth?.accountId]);

  // If files are loaded, try to resolve residency document URL for preview
  useEffect(() => {
    if (!form.residency_document) return;
    const match = fileData.find(
      (f) =>
        String(f.id) === String(form.residency_document) ||
        String(f.id) === String(form.residency_document?.file_id)
    );
    if (match) {
      setForm((prev) => ({
        ...prev,
        residency_document_url:
          match.url || match.name || prev.residency_document_url,
        residency_document_name:
          prev.residency_document_name || match.name || "",
      }));
    }
  }, [fileData, form.residency_document]);

  const onSave = async () => {
    // Deprecated: onSave previously updated DB directly. Use Request Changes flow instead.
    console.warn("onSave called - switching to Request Changes flow.");
    await handleRequestChanges();
  };

  // helper: compute changed fields between originalProfile and current form
  const getChangedFields = (original = {}, current = {}) => {
    const changes = {};
    Object.keys(current).forEach((key) => {
      const origVal = original[key] == null ? "" : String(original[key]);
      let curVal = current[key] == null ? "" : String(current[key]);

      // Apply server mapping for residency_status before comparison
      let finalOrigVal = origVal;
      let finalCurVal = curVal;
      if (key === 'residency_status') {
        finalCurVal = mapUIResidencyToServer(curVal);
        finalOrigVal = mapUIResidencyToServer(origVal);
      }

      if (finalOrigVal !== finalCurVal) {
        changes[key] = {
          from: finalOrigVal || null,
          to: finalCurVal || null,
        };
      }
    });
    return changes;
  };

  // Request Changes: send only modified fields to backend and do NOT update DB
  const handleRequestChanges = async () => {
    try {
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      if (!accountId) {
        showError("Account not found. Please login again.");
        return;
      }

      const changes = getChangedFields(originalProfile || {}, form);
      if (!changes || Object.keys(changes).length === 0) {
        showError(
          "No changes detected. Please modify fields before requesting changes."
        );
        return;
      }

      // Ask user to confirm sending the request
      if (!(await showConfirm("Send request to office/admin for review?")))
        return;

      setSaving(true);
      const token = auth?.token || localStorage.getItem("token");
      // Collect attachments: prefer explicit URLs already stored in form (residency_document_url)
      // or resolve a matching file URL from fileData when we only have a file_id.
      const attachmentsList = [];
      if (form?.residency_document_url)
        attachmentsList.push(form.residency_document_url);
      else if (form?.residency_document) {
        const match = fileData.find(
          (f) =>
            String(f.id) === String(form.residency_document) ||
            String(f.id) === String(form.residency_document?.file_id)
        );
        if (match && match.url) attachmentsList.push(match.url);
      }

      const payload = {
        accountId: Number(accountId),
        changes,
        attachments: attachmentsList,
      };

      const controller = new AbortController();
      const timeoutMs = 20000;
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      let res;
      try {
        res = await fetch(`${BASE_URL}/api/temp-staff/request-change`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }

      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(j?.message || "Failed to send change request");
        return;
      }

      // Show confirmation dialog
      showSuccess(
        "Your request has been sent to our team for review and update."
      );

      // Persist attachments locally so user sees the uploaded/requested file until logout
      try {
        if (payload.attachments && payload.attachments.length) {
          const accountIdLocal = accountId;
          const key = `requested_attachments_${accountIdLocal}`;
          localStorage.setItem(key, JSON.stringify(payload.attachments));
          // reflect immediately in UI
          setForm((prev) => ({
            ...prev,
            residency_document_url:
              prev.residency_document_url || payload.attachments[0],
            residency_document_name:
              prev.residency_document_name ||
              payload.attachments[0].split("/").pop() ||
              "",
          }));
        }
      } catch (e) {
        console.warn("Failed to persist requested attachments locally", e);
      }
    } catch (err) {
      console.error("Request Changes error", err);
      if (err?.name === "AbortError") {
        showError(
          "Request timed out while saving. Please check your connection and try again."
        );
        return;
      }
      showError("Failed to send request. Please try again later.");
    } finally {
      setSaving(false);
    }
  };

  const handleResidencyFile = async (file) => {
    if (!file) return;
    const validationMessage = validateStaffDocumentFile(file);
    if (validationMessage) {
      showError(validationMessage);
      return;
    }
    const accountId = auth?.accountId || localStorage.getItem("accountId");
    if (!accountId) return showError("Account not found");

    // Determine docType from residency_status
    const docType =
      form.residency_status === "work_permit" ||
        form.residency_status === "study_permit" ||
        form.residency_status === "refugee"
        ? form.residency_status
        : null;
    if (!docType)
      return showError("Please select Residency Status before uploading");

    const fd = new FormData();
    fd.append("file", file);
    fd.append("docType", docType);

    try {
      const token = auth?.token || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Let axios set the multipart Content-Type (including boundary)
      const resp = await axios.post(
        `${BASE_URL}/api/registration/${accountId}/upload-and-link`,
        fd,
        { headers, withCredentials: true }
      );
      const data = resp?.data || {};
      if (!data || !data.file_id) {
        console.error("Upload response missing file_id", data);
        return showError(data?.message || "Upload failed");
      }

      // Update form with returned file id and preview url
      setForm((prev) => ({
        ...prev,
        residency_document: data.file_id,
        residency_document_name: file.name,
        residency_document_url: data.url || data.s3Key || "",
      }));
      // reload file list to reflect new file
      await loadQualifications();
      showSuccess("File uploaded successfully");
    } catch (err) {
      console.error(
        "Residency upload failed",
        err,
        err?.response?.data || null
      );
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "File upload failed. Please try again.";
      showError(msg);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      if (!accountId) return showError("Account not found");
      if (!(await showConfirm("Delete this file?"))) return;

      const token = auth?.token || localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${accountId}/files/${fileId}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.success) {
        throw new Error(j?.message || "Failed to delete file");
      }

      showSuccess("File deleted successfully");
      await loadQualifications();
    } catch (err) {
      console.error("Delete file failed", err);
      showError(err?.message || "Failed to delete file");
    }
  };

  const handleDownloadFile = async (fileRow) => {
    try {
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      if (!accountId || !fileRow?.id) return showError("File not found");

      const token = auth?.token || localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${accountId}/files/${fileRow.id}/download`,
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      if (!res.ok) {
        const error = await res
          .json()
          .catch(() => ({ message: "Failed to download file" }));
        throw new Error(error.message || "Failed to download file");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileRow.name || "file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download file failed", err);
      showError(err?.message || "Failed to download file");
    }
  };

  const loadFilePreview = async (fileRow) => {
    const accountId = auth?.accountId || localStorage.getItem("accountId");
    if (!accountId || !fileRow?.id) throw new Error("File not found");

    const token = auth?.token || localStorage.getItem("token");
    const res = await fetch(
      `${BASE_URL}/api/personnel/accounts/${accountId}/files/${fileRow.id}/download`,
      {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.message || "Failed to preview file");
    }
    return res.blob();
  };

  const handleDownloadForm = async (formRow) => {
    try {
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      if (!accountId || !formRow?.submission_id) {
        return showError("Form not found");
      }

      const token = auth?.token || localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${accountId}/forms/${formRow.submission_id}/download`,
        {
          credentials: "include",
          ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
        },
      );

      if (!res.ok) {
        const error = await res
          .json()
          .catch(() => ({ message: "Failed to download form" }));
        throw new Error(error.message || "Failed to download form");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${formRow.name || "form"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download form failed", err);
      showError(err?.message || "Failed to download form");
    }
  };

  const handleOpenEditFile = (fileRow) => {
    setEditingFileRow(fileRow);
  };

  const handleRequestFileChange = async ({
    fileRow,
    nextTypeId,
    nextTypeLabel,
    replacementFile,
    nextExpiryDate,
  }) => {
    try {
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      if (!accountId) return showError("Account not found. Please login again.");
      if (!fileRow?.id) return showError("File not found.");
      if (!nextTypeId || !nextTypeLabel) return showError("Select file type.");

      const currentTypeLabel = String(fileRow.type || "").trim();
      const hasTypeChange = currentTypeLabel !== String(nextTypeLabel).trim();
      const hasReplacementFile = Boolean(replacementFile);
      const currentExpiryDate = fileRow?.expiry_date
        ? String(fileRow.expiry_date).slice(0, 10)
        : "";
      const normalizedNextExpiryDate =
        String(nextTypeLabel).trim() === "SIN Document"
          ? String(nextExpiryDate || "").slice(0, 10)
          : "";
      const hasExpiryChange = currentExpiryDate !== normalizedNextExpiryDate;

      if (!hasTypeChange && !hasReplacementFile && !hasExpiryChange) {
        return showError("No changes detected. Please modify type or expiry.");
      }

      if (!(await showConfirm("Send request to office/admin for review?"))) {
        return;
      }

      setSaving(true);
      const token = auth?.token || localStorage.getItem("token");

      if (hasReplacementFile) {
        const form = new FormData();
        form.append("file", replacementFile);
        form.append("state", "Pending review");
        form.append("note", `Replacement requested for ${fileRow.name || `file ${fileRow.id}`}`);
        form.append("type_id", nextTypeId);
        if (String(nextTypeLabel).trim() === "SIN Document") {
          form.append("expiry_date", normalizedNextExpiryDate || "");
        }

        const uploadRes = await fetch(
          `${BASE_URL}/api/personnel/accounts/${accountId}/files`,
          {
            method: "POST",
            credentials: "include",
            body: form,
            ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
          },
        );
        const uploadJson = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok || !uploadJson?.success) {
          showError(uploadJson?.message || "Failed to upload replacement file");
          return;
        }

        showSuccess(
          "Replacement file uploaded and sent to our team for review.",
        );
        setEditingFileRow(null);
        await loadQualifications();
        return;
      }

      const payload = {
        accountId: Number(accountId),
        changes: {
          file_name: {
            from: null,
            to: fileRow.name || `File #${fileRow.id}`,
          },
          file_type: {
            from: currentTypeLabel || null,
            to: nextTypeLabel,
          },
          file_expiry_date: {
            from: currentExpiryDate || null,
            to: normalizedNextExpiryDate || null,
          },
          file_reference: {
            from: null,
            to: `File ID ${fileRow.id}`,
          },
        },
        attachments: fileRow.url ? [fileRow.url] : [],
      };

      const res = await fetch(`${BASE_URL}/api/temp-staff/request-change`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(j?.message || "Failed to send file change request");
        return;
      }

      showSuccess(
        "Your file change request has been sent to our team for review and update.",
      );
      setEditingFileRow(null);
      await loadQualifications();
    } catch (err) {
      console.error("File change request error", err);
      showError("Failed to send file change request. Please try again later.");
    } finally {
      setSaving(false);
    }
  };

  //!password reset handler
  const handlePasswordReset = async ({ currentPassword, newPassword }) => {
    try {
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      const token = auth?.token || localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/my-account/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            currentPassword,
            newPassword,
          }),
        }
      );
      const j = await res.json();
      if (!res.ok || !j?.success) {
        throw new Error(j?.message || "Password reset failed");
      }
      showSuccess(j.message || "Password updated successfully");
      setOpenModal(null);
    } catch (e) {
      console.error("Password reset error", e);
      showError(e.message || "Failed to update password");
      throw e;
    }
  };

  const handleRemoveDevice = async (deviceId) => {
    if (
      !(await showConfirm(
        "Are you sure you want to remove this trusted device?"
      ))
    )
      return;

    try {
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      const token = auth?.token || localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/my-account/account/security/device/${deviceId}?accountId=${accountId}`,
        {
          method: "DELETE",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      const j = await res.json();
      if (!j?.success) throw new Error(j?.message || "Device removal failed");

      // Update local state
      setSecurityData((prev) => ({
        ...prev,
        devices: prev.devices.filter((d) => d.trusted_device_id !== deviceId),
      }));
      showSuccess("Device removed successfully");
    } catch (e) {
      console.error("Device removal error", e);
      showError("Failed to remove device");
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setSaving(true);
      const token = auth?.token || localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${BASE_URL}/api/my-account/account/avatar`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      const json = await res.json();
      if (json.success && json.url) {
        setForm((prev) => ({ ...prev, profile_picture: json.url }));
        showSuccess("Profile picture updated successfully");
      } else {
        showError(json.message || "Failed to upload profile picture");
      }
    } catch (err) {
      console.error("Avatar upload error:", err);
      showError("Failed to upload profile picture");
    } finally {
      setSaving(false);
      if (e.target) e.target.value = "";
    }
  };

  return (
    <Layout active="MyAccount">
      <style jsx>{`
        .PhoneInput {
          border: 1px solid rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          padding: 8px 12px;
          background: white;
          height: 40px;
          display: flex;
          align-items: center;
        }
        .PhoneInput:focus-within {
          outline: none;
          border-color: #dc1f00;
          box-shadow: 0 0 0 1px #dc1f00;
        }
        .PhoneInputInput {
          border: none;
          outline: none;
          background: transparent;
          font-size: 14px;
          padding: 0;
          margin-left: 8px;
          flex: 1;
        }
        .PhoneInputCountrySelect {
          border: none;
          background: transparent;
          outline: none;
          font-size: 14px;
          margin-right: 8px;
        }
      `}</style>
      <div className="py-3 px-2 flex flex-col flex-1 min-h-0 overflow-hidden no-mobile-table-stack">
        {/* Header */}
        <header className="flex items-center justify-between ml-6 mb-4 ml-2">
          <div className="flex items-center gap-3">
            <BackButton />
            <h1 className="text-2xl font-semibold text-foreground ">
              {loading
                ? "Loading..."
                : form.first_name || form.last_name
                  ? `${form.first_name} ${form.last_name}`.trim()
                  : "My Account"}
            </h1>
          </div>
        </header>
        <div className="mx-6 mb-4">
          {/* <button
            className="px-3 py-1 rounded bg-[#DC1F00] text-white text-sm flex items-center gap-2"
            onClick={() => setOpenFilterPanel(true)}
          >
            <FaFilter />
            Filter
          </button> */}
        </div>

        <div className="h-[1px] bg-[#a15d33] mx-6"></div>

        <div
          className={`mt-6 mb-4 mx-6 p-4 border-2 border-gray-200 rounded-xl flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden ${
            selectedTab === "FILES" ? fileFilterPanel.contentOffsetClass : ""
          }${selectedTab === "FORMS" ? formFilterPanel.contentOffsetClass : ""}`}
        >
          {/* Tabs */}
          <div className="flex space-x-8 mb-6 border-b border-[#DC1F004D] flex-shrink-0 overflow-x-auto">
            {[
              "GENERAL INFORMATION",
              "CONTACT",
              "PAYROLL",
              "SIGN IN & SECURITY",
              "QUALIFICATIONS",
              "FILES",
              "FORMS",
            ].map((tab) => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab)}
                className={`pb-2 px-1 text-sm text-[#DC1F00] font-medium border-b-2 transition-colors ${selectedTab === tab
                  ? "border-[#DC1F00] text-sidebar-active"
                  : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>
          {selectedTab === "GENERAL INFORMATION" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="p-6">
                <label className="block text-sm text-[#00000080] mb-2">
                  Profile Picture
                </label>
                <div className="relative w-16 h-16 mb-4">
                  {/* Profile Circle */}
                  <img
                    src={resolveBackendUrl(form.profile_picture) || DEFAULT_AVATAR_SRC}
                    alt="Profile"
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-300"
                    onError={(e) => {
                      e.target.src = DEFAULT_AVATAR_SRC;
                    }}
                  />
                  {/* Camera Icon Overlay */}
                  <label className="absolute bottom-0 right-0 bg-red-600 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer border-2 border-white hover:bg-red-700">
                    <ProfilePhotoInput
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                      disabled={saving}
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 7h4l3-3h4l3 3h4v12H3V7z"
                      />
                    </svg>
                  </label>
                </div>
                <label className="block text-sm text-[#00000080] mb-2">
                  Preferred Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.preferred_name}
                  onChange={handleChange("preferred_name")}
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 focus:ring-1 focus:ring-red-400"
                />
                <p className="text-xs text-[#00000080] mt-1 mb-4">
                  Only if different from First Name
                </p>
                <label className="block text-sm text-[#00000080] mb-2">
                  Date of Birth
                </label>
                <DateInput
                  name="dob"
                  value={form.birthdate}
                  onChange={(value) => handleChange("birthdate")({ target: { value } })}
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
              </div>

              {/* second column */}
              <div className="p-4 mt-1 lg:mt-6">
                <label className="block text-sm text-[#00000080] mb-2">
                  First Name <span className="text-[#00000080]">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.first_name}
                  onChange={handleChange("first_name")}
                  required
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
                <label className="block text-sm text-[#00000080] mb-2">
                  Last Name <span className="text-[#00000080]">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={form.last_name}
                  onChange={handleChange("last_name")}
                  required
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
                <label className="block text-sm text-[#00000080] mb-2">
                  Middle Name
                </label>
                <input
                  type="text"
                  name="middleName"
                  value={form.middle_name}
                  onChange={handleChange("middle_name")}
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
              </div>

              {/* third column */}
              <div className="p-4 mt-1 lg:mt-6">
                <label className="block text-sm text-[#00000080] mb-2">
                  Email <span className="text-[#00000080]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange("email")}
                  required
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
                <label className="block text-sm text-[#00000080] mb-2">
                  Gender
                </label>
                <select
                  name="Gender"
                  className="w-full border border-[#00000033] rounded-lg px-3 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                  value={form.gender || ""}
                  onChange={handleChange("gender")}
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          )}

          {/* CONTACT section */}
          {selectedTab === "CONTACT" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* first column */}
              <div className="p-6">
                <label className="block text-sm text-[#00000080] mb-2">Mobile Number <span className="text-[#00000080]">*</span></label>
                <div className="mb-6">
                  <PhoneInput
                    value={form.phone_mobile}
                    onChange={(value) => setForm(prev => ({ ...prev, phone_mobile: value }))}
                    defaultCountry="CA"
                    international
                    withCountryCallingCode
                    countryCallingCodeEditable={false}
                  />
                </div>
                <label className="block text-sm text-[#00000080] mb-2">Address <span className="text-[#00000080]">*</span></label>
                <AddressAutocomplete
                  value={{
                    street: form?.street || "",
                    address2: form?.address2 || "",
                    city: form?.city || "",
                    region: form?.region || "",
                    zip: form?.zip || "",
                    country: form?.country || "",
                    address: form?.address || "",
                  }}
                  onChange={(v) => {
                    const next = normalizeTempStaffAddressValue(v);
                    setForm((prev) => ({
                      ...(prev || {}),
                      ...next,
                    }));
                  }}
                />
                <input type="hidden" name="address" value={form?.address || ''} />
              </div>

              {/* second column */}
              <div className="p-6">
                <label className="block text-sm text-[#00000080] mb-2">
                  Emergency Contact Name{" "}
                  <span className="text-[#00000080]">*</span>
                </label>
                <input
                  type="text"
                  name="emergencyContactName"
                  value={form.emergency_name}
                  onChange={handleChange("emergency_name")}
                  required
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-6 focus:ring-1 focus:ring-red-400"
                />
                <label className="block text-sm text-[#00000080] mb-2">
                  Emergency Contact No{" "}
                  <span className="text-[#00000080]">*</span>
                </label>
                <div className="mb-6">
                  <PhoneInput
                    value={form.emergency_phone}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, emergency_phone: value }))
                    }
                    defaultCountry="CA"
                    international
                    withCountryCallingCode
                    countryCallingCodeEditable={false}
                  />
                </div>
              </div>

              {/* third column */}
              {/* <div className="p-6">
              <label className="block text-sm text-[#00000080] mb-2">Personal No <span className="text-[#00000080]">*</span></label>
              <div className="mb-6">
                <PhoneInput
                  value={form.personal_number}
                  onChange={(value) => setForm(prev => ({ ...prev, personal_number: value }))}
                  defaultCountry="CA"
                  international
                  withCountryCallingCode
                  countryCallingCodeEditable={false}
                />
              </div>
              <label className="block text-sm text-[#00000080] mb-2">Personal Email <span className="text-[#00000080]">*</span></label>
              <input
                type="email"
                name="PersonalEmail"
                value={form.personal_email}
                onChange={handleChange('personal_email')}
                required
                className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-6 focus:ring-1 focus:ring-red-400"
              />
            </div> */}
            </div>
          )}

          {/* PAYROLL section */}
          {selectedTab === "PAYROLL" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 items-start">
              {/* first column */}
              <div className="p-4">
                <label className="block text-sm text-[#00000080] mb-2">
                  Residency Status <span className="text-[#00000080]">*</span>
                </label>
                <select
                  name="Status"
                  required
                  className="w-full border border-[#00000033] rounded-lg px-3 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                  value={form.residency_status || ""}
                  onChange={handleChange("residency_status")}
                >
                  <option value="">Select</option>
                  <option value="canadian">Canadian Citizen / Permanent Resident</option>
                  <option value="work_permit">Work permit</option>
                  <option value="study_permit">Study permit</option>
                  <option value="refugee">Refugee</option>
                </select>
                {/* Residency document upload for Work / Study Permit and Refugee */}
                {(form.residency_status === "work_permit" ||
                  form.residency_status === "study_permit" ||
                  form.residency_status === "refugee") && (
                    <div className="profile-form-group">
                      <label className="block text-sm text-[#00000080] mb-2">
                        {form.residency_status === "work_permit"
                          ? "Work Permit *"
                          : form.residency_status === "study_permit"
                            ? "Study Permit *"
                            : "Refugee Document *"}
                      </label>

                      <div
                        className="residency-upload-box w-full border border-[#00000033] rounded-lg px-3 py-4 mb-3 bg-white"
                        onDrop={(e) => {
                          e.preventDefault();
                          const f =
                            e.dataTransfer.files && e.dataTransfer.files[0];
                          if (f) handleResidencyFile(f);
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        style={{
                          minHeight: 100,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        <div className="text-muted text-sm">
                          Drag & Drop your file or use the button below
                        </div>
                        <div className="text-xs text-muted">
                          {form.residency_document_name || "No file selected"}
                        </div>
                        <input
                          id="residencyFileTemp"
                          type="file"
                          accept={STAFF_DOCUMENT_ACCEPT}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const f = e.target.files && e.target.files[0];
                            if (f) handleResidencyFile(f);
                          }}
                        />
                        <label
                          htmlFor="residencyFileTemp"
                          className="mt-2 inline-block px-4 py-2 bg-buttonRed text-white rounded-3xl cursor-pointer"
                        >
                          Upload File
                        </label>
                      </div>

                      {form.residency_document_url && (
                        <div className="mt-2 text-sm">
                          <div className="font-medium">Uploaded Document</div>
                          <div className="mt-1">
                            {String(form.residency_document_url).endsWith(
                              ".pdf"
                            ) ||
                              String(form.residency_document_url).includes(
                                "pdf"
                              ) ? (
                              <a
                                href={form.residency_document_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 underline"
                              >
                                View PDF
                              </a>
                            ) : (
                              <img
                                src={form.residency_document_url}
                                alt="residency"
                                className="max-w-xs max-h-48 border rounded"
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                <label className="block text-sm text-[#00000080] mb-2">
                  HST NO <span className="text-[#00000080]">*</span>
                </label>
                <input
                  type="text"
                  name="hstNo"
                  value={form.hst_number}
                  onChange={handleChange("hst_number")}
                  required
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
              </div>

              {/* second column */}
              <div className="p-4">
                <label className="block text-sm text-[#00000080] mb-2">
                  Payment Delivery Method
                </label>
                <label className="flex items-center gap-1 text-sm text-[#0000000]">
                  <input
                    type="radio"
                    name="paymentMethod"
                    className="w-4 h-4 accent-[#DC1F00] mr-2"
                    checked={form.cheque_method === "delivery_pickup"}
                    onChange={() =>
                      setForm((f) => ({
                        ...f,
                        cheque_method: "delivery_pickup",
                      }))
                    }
                  />
                  Pickup cheque at office
                </label>
                <p className="text-xs text-[#00000080] mt-1 mb-4">
                  To request alternative options, please ask at the office.
                </p>

                <label className="block text-sm text-[#00000080] mb-2">
                  Applicable Tax Form
                </label>
                <div className="flex items-center gap-8">
                  <label className="flex items-center gap-1 text-sm text-[#00000080]">
                    <input
                      type="radio"
                      name="taxFormMethod"
                      className="w-4 h-4 accent-[#DC1F00] mr-2"
                      checked={form.applicable_tax_form === "T4"}
                      onChange={() =>
                        setForm((f) => ({ ...f, applicable_tax_form: "T4" }))
                      }
                    />
                    T4
                  </label>
                  <label className="flex items-center gap-1 text-sm text-[#00000080]">
                    <input
                      type="radio"
                      name="taxFormMethod"
                      className="w-4 h-4 accent-[#DC1F00] mr-2"
                      checked={form.applicable_tax_form === "T4A"}
                      onChange={() =>
                        setForm((f) => ({ ...f, applicable_tax_form: "T4A" }))
                      }
                    />
                    T4A
                  </label>
                  <label className="flex items-center gap-1 text-sm text-[#00000080]">
                    <input
                      type="radio"
                      name="taxFormMethod"
                      className="w-4 h-4 accent-[#DC1F00] mr-2"
                      checked={form.applicable_tax_form === "None"}
                      onChange={() =>
                        setForm((f) => ({ ...f, applicable_tax_form: "None" }))
                      }
                    />
                    None
                  </label>
                </div>
              </div>

              {/* third column */}
              <div className="p-4">
                <label className="block text-sm text-[#00000080] mb-2">
                  Social Insurance NO{" "}
                  <span className="text-[#00000080]">*</span>
                </label>
                <input
                  type="text"
                  name="socialNo"
                  value={form.sin}
                  onChange={handleChange("sin")}
                  required
                  className="w-full border border-[#00000033] rounded-lg px-2 py-2 mb-4 focus:ring-1 focus:ring-red-400"
                />
              </div>
            </div>
          )}

          {/* SIGN IN & SECURITY section */}
          {selectedTab === "SIGN IN & SECURITY" && (
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <MdEmail className="text-[#00000080] text-lg" />
                <span className="font-medium text-[#00000080]">Email:</span>
                <span className="text-[#000000]">
                  {securityData.email || "Not available"}
                </span>

                <FaLock className="text-[#00000080] text-lg ml-8" />
                <span className="font-medium text-[#00000080] ">Password:</span>
                <button
                  onClick={() => setOpenModal("reset")}
                  className="bg-[#DC1F0033] text-[#DC1F00] px-4 py-1 rounded-3xl hover:bg-[#CC2100] hover:text-white text-sm"
                >
                  Reset Password
                </button>

                {openModal === "reset" && (
                  <ResetPasswordForm
                    onClose={() => setOpenModal(null)}
                    onSave={handlePasswordReset}
                  />
                )}
              </div>

              <p className="font-medium text-[#00000080] mb-8">
                Last Sign In:{" "}
                <span className="text-[#000000] ml-4">
                  {securityData.last_signin
                    ? formatAppDateTime(securityData.last_signin, { emptyValue: "Never" })
                    : "Never"}
                </span>
              </p>

              <TwoStepVerificationSection
                userDisplayName={`${form.first_name || ""} ${form.last_name || ""}`.trim()}
              />

              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-gray-600">🛡️</span>
                  <h2 className="text-lg font-semibold">Trusted Devices</h2>
                </div>
                {securityData.devices && securityData.devices.length > 0 ? (
                  <div className="space-y-4">
                    {securityData.devices.map((device) => {
                      const deviceInfo = formatDeviceInfo(device);
                      return (
                        <div
                          key={device.trusted_device_id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              {/* Device Type and OS */}
                              <div className="flex items-center gap-3 mb-3">
                                <div className="flex items-center gap-2">
                                  {getDeviceTypeIcon(device.device_type, 20)}
                                  <span className="text-sm font-medium text-gray-700">
                                    {deviceInfo.deviceName}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {getOsIcon(device.os_type, 18)}
                                  <span className="text-sm text-gray-600">
                                    {deviceInfo.osName}
                                  </span>
                                </div>
                              </div>

                              {/* Browser */}
                              <div className="flex items-center gap-2 mb-2">
                                {getBrowserIcon(device.browser_type, 18)}
                                <span className="text-sm text-gray-600">
                                  {deviceInfo.browserName}
                                </span>
                              </div>

                              {/* Location */}
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-gray-500">📍</span>
                                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                                  <span>{deviceInfo.location}</span>
                                  {(deviceInfo.countryCode ||
                                    device.country) && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                        <span className="text-xs font-semibold">
                                          {(
                                            deviceInfo.countryCode ||
                                            device.country ||
                                            ""
                                          )
                                            .toString()
                                            .toUpperCase()}
                                        </span>
                                        {deviceInfo.countryName && (
                                          <span className="text-xs">
                                            {deviceInfo.countryName}
                                          </span>
                                        )}
                                      </span>
                                    )}
                                </div>
                              </div>

                              {/* Last Activity */}
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">🕒</span>
                                <span className="text-sm text-gray-500">
                                  Last activity:{" "}
                                  {formatAppDateTime(device.activity_date, { emptyValue: "Unknown" })}
                                </span>
                              </div>
                            </div>

                            {/* Remove Button */}
                            <button
                              onClick={() =>
                                handleRemoveDevice(device.trusted_device_id)
                              }
                              className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-2 rounded-md hover:bg-red-50 transition-colors border border-red-200 hover:border-red-300"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-gray-400 text-4xl mb-3">🔒</div>
                    <div className="text-gray-500 text-lg font-medium mb-2">
                      No trusted devices found
                    </div>
                    <div className="text-gray-400 text-sm">
                      Devices you've logged in from will appear here
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* QUALIFICATIONS section */}
          {selectedTab === "QUALIFICATIONS" && (
            <div className="px-6 py-2">
              <div className="flex flex-col sm:flex-row sm:justify-between items-start mb-10 gap-4 sm:gap-0">
                <div>
                  <h2 className="text-lg font-semibold mb-2">Skills</h2>
                  <ul className="ml-2 list-inside text-base text-textGray space-y-1">
                    {skillData.map((item, index) => (
                      <li key={item.skill_id || index}>
                        {item.skill_name || item.skill}
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  className="bg-buttonRed hover:bg-buttonRed-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60"
                  onClick={() => setOpenModal("addskill")}
                >
                  ADD SKILL
                </button>
                {openModal === "addskill" && (
                  <AddSkillForm
                    accountId={
                      auth?.accountId || localStorage.getItem("accountId")
                    }
                    onClose={() => {
                      setOpenModal(null);
                      loadQualifications();
                    }}
                  />
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between items-start gap-4 sm:gap-0">
                <div>
                  <h2 className="text-lg font-semibold mb-1">Certificate</h2>
                  <span className="text-xs font-medium mb-1">
                    Most Recent :{" "}
                  </span>
                </div>
                <button
                  className="bg-buttonRed hover:bg-buttonRed-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60"
                  onClick={() => {
                    setEditingCertificate(null);
                    setOpenModal("addsCertificate");
                  }}
                >
                  ADD CERTIFICATE
                </button>
                {openModal === "addsCertificate" && (
                  <AddCertificateForm
                    accountId={
                      auth?.accountId || localStorage.getItem("accountId")
                    }
                    onClose={() => {
                      setOpenModal(null);
                      setEditingCertificate(null);
                      loadQualifications();
                    }}
                    initialData={editingCertificate}
                    mode={editingCertificate ? "edit" : "add"}
                    province={form?.region}
                  />
                )}
              </div>

              <div className="atlas-list-table-wrap bg-card rounded-lg border border-border overflow-x-auto mt-4">
                <table className="client-files-table w-full min-w-[640px] border-collapse !table-auto">
                  <colgroup>
                    <col style={{ width: "35%" }} />
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "5%" }} />
                  </colgroup>
                  <thead className="bg-[#dc1f0080] text-white sticky top-0 z-20">
                    <tr>
                      <th className="py-2 px-3 text-left text-xs font-medium text-white align-top">
                        Certification
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-white align-top">
                        Supporting File
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-white align-top whitespace-nowrap">
                        Expiry
                      </th>
                      <th className="py-2 px-3 text-right text-xs font-medium text-white align-top whitespace-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificateData.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center text-gray-500 py-8">
                          No certificates found
                        </td>
                      </tr>
                    )}
                    {certificateData.map((row) => (
                      <tr
                        key={row.id}
                        className="border-t border-border hover:bg-table-row-hover transition-colors align-top"
                      >
                        <td className="client-files-name-cell py-2 px-3 align-top">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingCertificate(row);
                              setOpenModal("addsCertificate");
                            }}
                            className="text-left text-sm font-medium text-buttonRed hover:underline cursor-pointer break-words [overflow-wrap:anywhere]"
                          >
                            {certDisplayName(row.name || row.certificate, form?.region)}
                          </button>
                        </td>
                        <td className="py-2 px-3 text-sm text-foreground break-words [overflow-wrap:anywhere] align-top">
                          {getDisplayFileName(row.file) || "—"}
                        </td>
                        <td className="py-2 px-3 text-sm text-foreground align-top whitespace-nowrap">
                          {row.expDate || "—"}
                        </td>
                        <td className="py-2 px-3 text-right align-top whitespace-nowrap">
                          <div className="flex justify-end">
                            {row.file ? (
                              <ActionDropdown
                                actions={[
                                  {
                                    label: "Download",
                                    onClick: () => handleDownloadCertificateFile(row),
                                  },
                                ]}
                              />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* FILES section */}
          {selectedTab === "FILES" && (
            <div className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden">
              <div className="flex-shrink-0">
                <AtlasListToolbar
                  searchValue={searchTerm}
                  onSearchChange={setSearchTerm}
                  searchPlaceholder="Search files"
                  onFilterClick={fileFilterPanel.toggle}
                  filterOpen={fileFilterPanel.isOpen}
                  onRefresh={loadQualifications}
                  refreshSpinning={qualificationsLoading}
                resultsMeta={{
                  startRow: fileResultsStart,
                  endRow: fileResultsEnd,
                  totalCount: sortedFileRows.length,
                  pageSize: filePageSize,
                  pageSizeOptions: [10, 25, 50, 100],
                  onPageSizeChange: setFilePageSize,
                  sortBy: fileSortBy,
                  sortOrder: fileSortDirection,
                  sortOptions: TEMP_STAFF_FILES_SORT_OPTIONS,
                  onSortByChange: setFileSortBy,
                  onSortOrderToggle: () =>
                    setFileSortDirection((prev) => (prev === "asc" ? "desc" : "asc")),
                }}
                chipBar={
                  <AtlasFilterChipBar
                    chips={fileFilterChips}
                    onChipClick={(chip) => {
                      if (chip.fieldId) fileFilterPanel.focusField(chip.fieldId);
                      else fileFilterPanel.open();
                    }}
                    onRemove={removeFileFilterChip}
                    onClearAll={clearFileFilters}
                  />
                }
                actions={
                  <>
                    <button
                      type="button"
                      onClick={() => setOpenModal("addfile")}
                      className="bg-buttonRed hover:bg-buttonRed-hover text-white px-4 py-2 rounded-3xl text-sm font-medium transition-colors"
                    >
                      CREATE
                    </button>
                    {openModal === "addfile" && (
                      <AddFileForm
                        accountId={
                          auth?.accountId || localStorage.getItem("accountId")
                        }
                        onClose={() => {
                          setOpenModal(null);
                          loadQualifications();
                        }}
                      />
                    )}
                  </>
                }
              />
              </div>

              <div className="flex-1 min-h-0 min-w-0 mt-3 overflow-hidden">
                {qualificationsLoading ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    Loading files…
                  </div>
                ) : sortedFileRows.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    No files found for the selected filters.
                  </div>
                ) : (
                  <div className="atlas-list-table-wrap bg-card rounded-lg border border-border h-full min-h-0 overflow-auto">
                    <table className="client-files-table w-full min-w-0 md:min-w-[760px] border-collapse block md:table !table-auto">
                      <colgroup className="hidden md:table-column-group">
                        <col style={{ width: "30%" }} />
                        <col style={{ width: "22%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "6%" }} />
                      </colgroup>
                      <thead className="bg-[#dc1f0080] text-white sticky top-0 z-20 hidden md:table-header-group">
                        <tr>
                          <th className="py-2 px-3 text-left text-xs font-medium text-white align-top">
                            <button
                              type="button"
                              onClick={() => handleFileHeaderSort("name")}
                              className="inline-flex items-center hover:underline text-left"
                            >
                              Name
                              {renderFileSortArrow("name")}
                            </button>
                          </th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-white align-top">
                            Note
                          </th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-white align-top whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleFileHeaderSort("state")}
                              className="inline-flex items-center hover:underline"
                            >
                              State
                              {renderFileSortArrow("state")}
                            </button>
                          </th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-white align-top whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleFileHeaderSort("type")}
                              className="inline-flex items-center hover:underline"
                            >
                              Type
                              {renderFileSortArrow("type")}
                            </button>
                          </th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-white align-top whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleFileHeaderSort("create_date")}
                              className="inline-flex items-center hover:underline"
                            >
                              Created
                              {renderFileSortArrow("create_date")}
                            </button>
                          </th>
                          <th className="py-2 px-3 text-right text-xs font-medium text-white align-top whitespace-nowrap">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="block md:table-row-group">
                        {pagedFileRows.map((row) => {
                          const normalizedState = formatFileState(row.state);
                          const isDeleted =
                            normalizedState.toLowerCase() === "deleted";
                          return (
                            <tr
                              key={row.id}
                              className="border border-border md:border-0 md:border-t hover:bg-table-row-hover transition-colors align-top block md:table-row mb-4 md:mb-0 p-4 md:p-0 rounded-lg md:rounded-none bg-white md:bg-transparent"
                            >
                              <td className="client-files-name-cell py-2 px-3 align-top block md:table-cell">
                                <div className="flex md:hidden text-xs font-semibold text-gray-500 mb-1">Name</div>
                                <button
                                  type="button"
                                  onClick={() => handleOpenEditFile(row)}
                                  className="text-left text-sm font-medium text-red-600 hover:underline cursor-pointer break-words [overflow-wrap:anywhere]"
                                >
                                  {row.name || "Untitled"}
                                </button>
                              </td>
                              <td className="py-2 px-3 text-sm text-muted-foreground break-words [overflow-wrap:anywhere] align-top block md:table-cell">
                                <div className="flex md:hidden text-xs font-semibold text-gray-500 mb-1 mt-2">Note</div>
                                {row.note || ""}
                              </td>
                              <td className="py-2 px-3 text-sm text-foreground capitalize align-top whitespace-nowrap block md:table-cell">
                                <div className="flex md:hidden text-xs font-semibold text-gray-500 mb-1 mt-2">State</div>
                                {normalizedState}
                              </td>
                              <td className="py-2 px-3 text-sm text-foreground align-top break-words [overflow-wrap:anywhere] block md:table-cell">
                                <div className="flex md:hidden text-xs font-semibold text-gray-500 mb-1 mt-2">Type</div>
                                {row.type || ""}
                              </td>
                              <td className="py-2 px-3 text-sm text-foreground align-top whitespace-nowrap block md:table-cell">
                                <div className="flex md:hidden text-xs font-semibold text-gray-500 mb-1 mt-2">Created</div>
                                {formatAppDateTime(row.create_date, { emptyValue: "" })}
                              </td>
                              <td className="py-2 px-3 text-right align-top whitespace-nowrap block md:table-cell">
                                <div className="flex justify-start md:justify-end mt-3 md:mt-0 pt-3 md:pt-0 border-t md:border-0 border-gray-100">
                                  <ActionDropdown
                                    actions={
                                      isDeleted
                                        ? []
                                        : [
                                            {
                                              label: "Preview",
                                              onClick: () => setPreviewFileRow(row),
                                            },
                                            {
                                              label: "Download",
                                              onClick: () => handleDownloadFile(row),
                                            },
                                          ]
                                    }
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <TempStaffEditFileModal
                isOpen={Boolean(editingFileRow)}
                fileRow={editingFileRow}
                fileTypeCatalog={fileTypeCatalog}
                onClose={() => setEditingFileRow(null)}
                onDownload={handleDownloadFile}
                onPreview={setPreviewFileRow}
                onRequestChange={handleRequestFileChange}
                saving={saving}
              />
              <DocumentPreviewModal
                isOpen={Boolean(previewFileRow)}
                file={previewFileRow}
                loadPreview={loadFilePreview}
                onClose={() => setPreviewFileRow(null)}
              />
            </div>
          )}

          {/* FORMS section */}
          {selectedTab === "FORMS" && (
            <div className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden">
              <div className="flex-shrink-0">
                <AtlasListToolbar
                  searchValue={formSearchTerm}
                  onSearchChange={setFormSearchTerm}
                  searchPlaceholder="Search forms"
                  onFilterClick={formFilterPanel.toggle}
                  filterOpen={formFilterPanel.isOpen}
                  onRefresh={loadQualifications}
                  refreshSpinning={qualificationsLoading}
                  resultsMeta={{
                    startRow: formResultsStart,
                    endRow: formResultsEnd,
                    totalCount: sortedFormRows.length,
                    pageSize: formPageSize,
                    pageSizeOptions: [10, 25, 50, 100],
                    onPageSizeChange: setFormPageSize,
                    sortBy: formSortBy,
                    sortOrder: formSortDirection,
                    sortOptions: TEMP_STAFF_FORMS_SORT_OPTIONS,
                    onSortByChange: setFormSortBy,
                    onSortOrderToggle: () =>
                      setFormSortDirection((prev) => (prev === "asc" ? "desc" : "asc")),
                  }}
                  chipBar={
                    <AtlasFilterChipBar
                      chips={formFilterChips}
                      onChipClick={(chip) => {
                        if (chip.fieldId) formFilterPanel.focusField(chip.fieldId);
                        else formFilterPanel.open();
                      }}
                      onRemove={removeFormFilterChip}
                      onClearAll={clearFormFilters}
                    />
                  }
                />
              </div>

              <div className="flex-1 min-h-0 min-w-0 mt-3 overflow-hidden">
                {qualificationsLoading ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    Loading forms…
                  </div>
                ) : sortedFormRows.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    No forms found for the selected filters.
                  </div>
                ) : (
                  <div className="atlas-list-table-wrap bg-card rounded-lg border border-border h-full min-h-0 overflow-auto">
                    <table className="client-files-table w-full min-w-0 md:min-w-[640px] border-collapse block md:table !table-auto">
                      <colgroup className="hidden md:table-column-group">
                        <col style={{ width: "52%" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "22%" }} />
                        <col style={{ width: "8%" }} />
                      </colgroup>
                      <thead className="bg-[#dc1f0080] text-white sticky top-0 z-20 hidden md:table-header-group">
                        <tr>
                          <th className="py-2 px-3 text-left text-xs font-medium text-white align-top">
                            <button
                              type="button"
                              onClick={() => handleFormHeaderSort("name")}
                              className="inline-flex items-center hover:underline text-left"
                            >
                              Form
                              {renderFormSortArrow("name")}
                            </button>
                          </th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-white align-top whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleFormHeaderSort("state")}
                              className="inline-flex items-center hover:underline"
                            >
                              State
                              {renderFormSortArrow("state")}
                            </button>
                          </th>
                          <th className="py-2 px-3 text-left text-xs font-medium text-white align-top whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleFormHeaderSort("submit_date")}
                              className="inline-flex items-center hover:underline"
                            >
                              Submitted Date
                              {renderFormSortArrow("submit_date")}
                            </button>
                          </th>
                          <th className="py-2 px-3 text-right text-xs font-medium text-white align-top whitespace-nowrap">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="block md:table-row-group">
                        {pagedFormRows.map((row) => (
                          <tr
                            key={row.submission_id || row.form_id}
                            className="border border-border md:border-0 md:border-t hover:bg-table-row-hover transition-colors align-top block md:table-row mb-4 md:mb-0 p-4 md:p-0 rounded-lg md:rounded-none bg-white md:bg-transparent"
                          >
                            <td className="client-files-name-cell py-2 px-3 align-top block md:table-cell">
                              <div className="flex md:hidden text-xs font-semibold text-gray-500 mb-1">Form</div>
                              <span className="text-sm font-medium text-foreground break-words [overflow-wrap:anywhere]">
                                {row.name || "Untitled"}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-sm text-foreground capitalize align-top whitespace-nowrap block md:table-cell">
                              <div className="flex md:hidden text-xs font-semibold text-gray-500 mb-1 mt-2">State</div>
                              {row.state || ""}
                            </td>
                            <td className="py-2 px-3 text-sm text-foreground align-top whitespace-nowrap block md:table-cell">
                              <div className="flex md:hidden text-xs font-semibold text-gray-500 mb-1 mt-2">Submitted Date</div>
                              {formatAppDate(row.submit_date)}
                            </td>
                            <td className="py-2 px-3 text-right align-top whitespace-nowrap block md:table-cell">
                              <div className="flex justify-start md:justify-end mt-3 md:mt-0 pt-3 md:pt-0 border-t md:border-0 border-gray-100">
                                <ActionDropdown
                                  actions={[
                                    {
                                      label: "Download",
                                      onClick: () => handleDownloadForm(row),
                                    },
                                  ]}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {(selectedTab === "GENERAL INFORMATION" ||
            selectedTab === "CONTACT" ||
            selectedTab === "PAYROLL") && (
              <div className="flex flex-col sm:flex-row items-center sm:items-start mb-6 sm:mb-2 mx-4 sm:mx-0 gap-3 sm:gap-0 mt-4 sm:mt-0">
                <button
                  onClick={onSave}
                  disabled={saving}
                  className="sm:ml-4 bg-buttonRed hover:bg-buttonRed-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60"
                >
                  {saving ? "SAVING..." : "REQUEST CHANGES"}
                </button>
                <p className="sm:ml-4 py-1 text-xs text-center sm:text-left text-gray-500">
                  Requested change will be sent to our team to review and update.
                </p>
              </div>
            )}

          <AtlasFilterPanel
            isOpen={selectedTab === "FILES" && fileFilterPanel.isOpen}
            onClose={fileFilterPanel.close}
            onClear={clearFileFilters}
            activeCount={fileFilterChips.length}
          >
            <TempStaffFilesFilterFields
              focusedFieldId={fileFilterPanel.focusedFieldId}
              onFocusClear={fileFilterPanel.clearFocus}
              fileTypeOptions={fileTypeFilterOptions}
              fileTypeFilters={fileTypeFilters}
              setFileTypeFilters={setFileTypeFilters}
              statusFilters={fileStatusFilters}
              setStatusFilters={setFileStatusFilters}
              sortBy={fileSortBy}
              setSortBy={setFileSortBy}
              sortDirection={fileSortDirection}
              setSortDirection={setFileSortDirection}
            />
          </AtlasFilterPanel>

          <AtlasFilterPanel
            isOpen={selectedTab === "FORMS" && formFilterPanel.isOpen}
            onClose={formFilterPanel.close}
            onClear={clearFormFilters}
            activeCount={formFilterChips.length}
          >
            <TempStaffFormsFilterFields
              focusedFieldId={formFilterPanel.focusedFieldId}
              onFocusClear={formFilterPanel.clearFocus}
              stateOptions={formStateOptions}
              stateFilters={formStateFilters}
              setStateFilters={setFormStateFilters}
              sortBy={formSortBy}
              setSortBy={setFormSortBy}
              sortDirection={formSortDirection}
              setSortDirection={setFormSortDirection}
            />
          </AtlasFilterPanel>
        </div>
      </div>
    </Layout>
  );
};

export default MyAccount_tempStaff;
